import { Controller } from "@hotwired/stimulus"

// Polls the verification status endpoint while the page is visible. When
// verified, plays a success animation and redirects. Manual "I verified"
// button forces an immediate poll and falls back to a reload.
//
// Backoff: 2s → 4s → 8s → cap 15s. Hard stop after 5 minutes — UI shows a
// "still waiting?" hint and stops polling until the user taps the button.
// Visibility: polling pauses when the tab is hidden; resumes with a single
// immediate poll on focus (handles iOS app foregrounding after the user
// taps the verification link in Mail).
export default class extends Controller {
  static targets  = ["mascot", "button", "buttonLabel", "spinner", "successOverlay", "hint"]
  static values   = {
    statusUrl:        String,
    redirectFallback: String,
    maxMs:            { type: Number, default: 5 * 60 * 1000 },
    initialDelayMs:   { type: Number, default: 2000 },
    maxDelayMs:       { type: Number, default: 15000 }
  }

  connect() {
    this.startedAt    = Date.now()
    this.currentDelay = this.initialDelayMsValue
    this.timer        = null
    this.inFlight     = false
    this.stopped      = false
    this.success      = false

    this._onVisibility = this.handleVisibility.bind(this)
    document.addEventListener("visibilitychange", this._onVisibility)
    window.addEventListener("focus", this._onVisibility)

    this.scheduleNext(this.currentDelay)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this._onVisibility)
    window.removeEventListener("focus", this._onVisibility)
    this.clearTimer()
  }

  // Manual button: immediate poll, button shows spinner.
  async checkNow(event) {
    event?.preventDefault?.()
    if (this.success || this.inFlight) return

    this.setButtonLoading(true)
    const verified = await this.poll(true)
    if (!verified) {
      this.setButtonLoading(false)
      this.shakeButton()
      this.showHint()
    }
  }

  handleVisibility() {
    if (document.hidden) {
      this.clearTimer()
      return
    }
    if (this.stopped || this.success) return
    // App came back to foreground — poll immediately, then resume backoff.
    this.clearTimer()
    this.currentDelay = this.initialDelayMsValue
    this.poll(false).then(verified => {
      if (!verified && !this.stopped) this.scheduleNext(this.currentDelay)
    })
  }

  scheduleNext(delay) {
    this.clearTimer()
    if (this.stopped || this.success) return
    if (Date.now() - this.startedAt > this.maxMsValue) {
      this.stopped = true
      this.showHint()
      return
    }
    this.timer = setTimeout(() => {
      this.poll(false).then(verified => {
        if (verified || this.stopped) return
        this.currentDelay = Math.min(this.currentDelay * 2, this.maxDelayMsValue)
        this.scheduleNext(this.currentDelay)
      })
    }, delay)
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  async poll(forced) {
    if (this.inFlight) return false
    this.inFlight = true
    try {
      const res = await fetch(this.statusUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return false
      const data = await res.json()
      if (data.verified) {
        this.success = true
        this.clearTimer()
        await this.playSuccess()
        const dest = data.redirect_to || this.redirectFallbackValue
        if (dest) {
          if (window.Turbo) window.Turbo.visit(dest)
          else window.location.href = dest
        } else {
          window.location.reload()
        }
        return true
      }
      return false
    } catch (_) {
      return false
    } finally {
      this.inFlight = false
    }
  }

  setButtonLoading(loading) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = loading
    if (this.hasButtonLabelTarget) this.buttonLabelTarget.style.opacity = loading ? "0.4" : "1"
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = !loading
  }

  shakeButton() {
    if (!this.hasButtonTarget) return
    this.buttonTarget.classList.remove("evs-shake")
    void this.buttonTarget.offsetWidth
    this.buttonTarget.classList.add("evs-shake")
  }

  showHint() {
    if (this.hasHintTarget) this.hintTarget.hidden = false
  }

  async playSuccess() {
    if (this.hasMascotTarget) this.mascotTarget.classList.add("evs-mascot-celebrate")
    if (this.hasSuccessOverlayTarget) {
      this.successOverlayTarget.hidden = false
      requestAnimationFrame(() => this.successOverlayTarget.classList.add("evs-success-show"))
    }
    await new Promise(r => setTimeout(r, 850))
  }
}
